import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Alert,
  Chip,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { X, AlertCircle, FileText } from 'lucide-react';
import { performanceService } from '@/services/api';

interface MissReasonModalProps {
  open: boolean;
  onClose: () => void;
  data?: {
    taskId?: string;
    taskTitle?: string;
    missedDate?: string;
  };
}

type MissReasonType = 'blocker' | 'priority_change' | 'personal' | 'technical' | 'dependency' | 'other';

const REASON_TO_CATEGORY: Record<MissReasonType, 'blocked' | 'reprioritized' | 'underestimated' | 'external_dependency' | 'personal' | 'other'> = {
  blocker: 'blocked',
  priority_change: 'reprioritized',
  personal: 'personal',
  technical: 'other',
  dependency: 'external_dependency',
  other: 'other',
};

/**
 * MissReasonModal
 * Form for providing reason when missing a task deadline. Submits to backend performance API.
 */
const MissReasonModal: React.FC<MissReasonModalProps> = ({ open, onClose, data }) => {
  const [reasonType, setReasonType] = useState<MissReasonType>('blocker');
  const [description, setDescription] = useState('');
  const [newDeadline, setNewDeadline] = useState('');
  const [requiresHelp, setRequiresHelp] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!data?.taskId) {
      setSubmitError('Task is required to submit a miss reason.');
      return;
    }
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const notes = [
        newDeadline ? `Proposed new deadline: ${newDeadline}` : '',
        requiresHelp ? 'Requires help or additional resources.' : '',
      ].filter(Boolean).join(' ');
      const raw = data.missedDate ?? '';
      const date = /^\d{4}-\d{2}-\d{2}$/.test(raw) ? raw.slice(0, 10) : new Date().toISOString().slice(0, 10);
      await performanceService.submitMissReason(data.taskId, {
        reason: description,
        category: REASON_TO_CATEGORY[reasonType],
        notes: notes || undefined,
        date,
      });
      setSubmitted(true);
      setTimeout(() => handleClose(), 1500);
    } catch (err) {
      const message = (err as { response?: { data?: { message?: string } }; message?: string })?.response?.data?.message ?? (err as Error)?.message ?? 'Failed to submit reason.';
      setSubmitError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setReasonType('blocker');
    setDescription('');
    setNewDeadline('');
    setRequiresHelp(false);
    setSubmitted(false);
    setSubmitError(null);
    onClose();
  };

  const reasonTypes = [
    { value: 'blocker', label: 'Blocker / Dependency Issue' },
    { value: 'priority_change', label: 'Priority Changed' },
    { value: 'personal', label: 'Personal / Leave' },
    { value: 'technical', label: 'Technical Difficulty' },
    { value: 'dependency', label: 'Waiting on Others' },
    { value: 'other', label: 'Other' },
  ];

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AlertCircle size={20} color="#ed6c02" />
            <Typography variant="h6" fontWeight={600}>
              Missed Deadline Reason
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <X size={20} />
          </IconButton>
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent>
        {submitted ? (
          <Alert severity="success" sx={{ mt: 2 }}>
            Reason submitted successfully. Your manager has been notified.
          </Alert>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
            {submitError && (
              <Alert severity="error" onClose={() => setSubmitError(null)}>
                {submitError}
              </Alert>
            )}
            {/* Task Info */}
            {data?.taskTitle && (
              <Box
                sx={{
                  p: 2,
                  bgcolor: 'action.hover',
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <FileText size={18} />
                <Box>
                  <Typography variant="subtitle2">{data.taskTitle}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Original deadline: {data.missedDate || 'Not specified'}
                  </Typography>
                </Box>
              </Box>
            )}

            {/* Status chips */}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip label="Overdue" color="error" size="small" />
              <Chip label="Requires Explanation" color="warning" size="small" />
            </Box>

            {/* Reason Type */}
            <FormControl fullWidth>
              <InputLabel>Reason Type</InputLabel>
              <Select
                value={reasonType}
                label="Reason Type"
                onChange={(e) => setReasonType(e.target.value as MissReasonType)}
              >
                {reasonTypes.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Description */}
            <TextField
              label="Detailed Explanation"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              multiline
              rows={4}
              fullWidth
              required
              placeholder="Please provide a detailed explanation for missing the deadline..."
            />

            {/* New Deadline */}
            <TextField
              label="Proposed New Deadline"
              type="date"
              value={newDeadline}
              onChange={(e) => setNewDeadline(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: new Date().toISOString().split('T')[0] }}
            />

            {/* Help checkbox */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={requiresHelp}
                  onChange={(e) => setRequiresHelp(e.target.checked)}
                />
              }
              label="I need help or additional resources to complete this task"
            />

            {/* Warning */}
            <Alert severity="warning">
              Please provide an honest and accurate explanation. This information
              will be used for workload planning and process improvement.
            </Alert>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="warning"
          onClick={handleSubmit}
          disabled={isSubmitting || submitted || !description}
        >
          {isSubmitting ? 'Submitting...' : 'Submit Reason'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MissReasonModal;
